import { DataUsage, InsertDriveFile, MoreHoriz, Videocam, Image } from '@mui/icons-material';
import { Box, FormControl, InputLabel, ListItem, MenuItem, Select, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { KeyDriveSelect } from '../../../../../components/KeydriveComponents/Selects/KeyDriveSelect';
import StorageUsageChart from '../Charts/StorageUsageChart';
import { DashboardStorageBreakdownItem } from '../DashboardStorageBreakdownItem/DashboardStorageBreakdownItem';
import { useTranslation } from 'react-i18next';
import { StorageSizeInfoModel } from '../../../../../model/StorageSizeInfoModel';
import { GetOrganizationUserTrim, GetStorageSizeByOrgInternalName, GetStorageSizeByOrgInternalNamePerCategories } from '../../../../../services/OrganizationServices';
import { useParams } from 'react-router-dom';
import { User } from '../../../../../model/OrganizationModel';
import { StorageSizeInfoPerCategoriesVO } from '../../../../../model/StorageSizeInfoPerCategoriesVO ';

export const DashboardStoragePerUser: React.FC = () => {
    const { nameOrg } = useParams();
    const { t } = useTranslation();

    const [generalData, setGeneralData] = useState<StorageSizeInfoModel>({ usedSpace: 0, totalSpace: 0, freeSpace: 0 });
    const [loadingGeneralData, setLoadingGeneralData] = useState(false);

    const [storageSizeCategories, setStorageSizeCategories] = useState<StorageSizeInfoPerCategoriesVO>({
        documentosUsed: 0,
        imagensUsed: 0,
        videosUsed: 0,
        outrosUsed: 0,
    });
    const [loadingStorageSizeCategories, setLoadingStorageSizeCategories] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [userSelected, setUserSelected] = useState<string | null>(null);

    useEffect(() => {
        if (!nameOrg) return;

        GetOrganizationUserTrim(nameOrg, 0, 100)
            .then(data => data && setUsers(data))
            .catch(err => console.error(err));

    }, [nameOrg]);

    const fetchData = useCallback(async () => {
        if (!nameOrg) return;

        setLoadingGeneralData(true);
        setLoadingStorageSizeCategories(true);

        try {
            const [general, categories] = await Promise.all([
                userSelected && userSelected !== ''
                    ? GetStorageSizeByOrgInternalName(nameOrg, userSelected)
                    : GetStorageSizeByOrgInternalName(nameOrg),
                userSelected && userSelected !== ''
                    ? GetStorageSizeByOrgInternalNamePerCategories(nameOrg, userSelected)
                    : GetStorageSizeByOrgInternalNamePerCategories(nameOrg)
            ]);

            if (general) setGeneralData(general);
            if (categories) setStorageSizeCategories(categories);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingGeneralData(false);
            setLoadingStorageSizeCategories(false);
        }
    }, [nameOrg, userSelected]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return (
        <>
            <Box sx={{ display: "flex", gap: "8px" }}>
                <Box
                    width={24}
                    height={24}
                    sx={{ backgroundColor: "#e8eff3", display: "flex", justifyContent: "center", alignItems: "center", borderRadius: 2 }}
                >
                    <DataUsage fontSize="small" sx={{ color: "#22638D" }} />
                </Box>
                <Typography
                    sx={{
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: 16,
                        lineHeight: "21px",
                        color: "#2D2D2D",
                        display: "inline-block",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                    }}
                >
                    {t("Dashboard.storagePerUser")}
                </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <StorageUsageChart loading={loadingGeneralData} used={generalData.usedSpace} free={generalData.freeSpace} total={generalData.totalSpace} />
            </Box>

            <Box sx={{ marginTop: 1 }}>

                <FormControl fullWidth size="small">
                    <InputLabel id="demo-select-small-label">{t("Dashboard.Users")}</InputLabel>
                    <Select
                        labelId="demo-select-small-label"
                        id="demo-select-small"
                        value={!userSelected?'':userSelected}
                        label={t("Dashboard.Users")}
                    >
                        <MenuItem value={''} onClick={() => setUserSelected('')}>{t("Dashboard.AllUsers")}</MenuItem>
                        {users.map((user) => (
                            <MenuItem key={user.id} value={user.id} onClick={() => setUserSelected(user.id)}>{user.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box sx={{ marginTop: "8px" }}>
                    <DashboardStorageBreakdownItem loading={loadingStorageSizeCategories} icon={<InsertDriveFile fontSize="small" sx={{ color: "#22638D" }} />} name={t("StorageTypeFile.documents")} usedSpace={storageSizeCategories.documentosUsed} />
                    <DashboardStorageBreakdownItem loading={loadingStorageSizeCategories} icon={<Image fontSize="small" sx={{ color: "#22638D" }} />} name={t("StorageTypeFile.images")} usedSpace={storageSizeCategories.imagensUsed} />
                    <DashboardStorageBreakdownItem loading={loadingStorageSizeCategories} icon={<Videocam fontSize="small" sx={{ color: "#22638D" }} />} name={t("StorageTypeFile.videos")} usedSpace={storageSizeCategories.videosUsed} />
                    <DashboardStorageBreakdownItem loading={loadingStorageSizeCategories} icon={<MoreHoriz fontSize="small" sx={{ color: "#22638D" }} />} name={t("StorageTypeFile.others")} usedSpace={storageSizeCategories.outrosUsed} />
                </Box>
            </Box>
        </>
    );
};
